import React from "react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import classnames from "classnames";
import { useTheme } from "react-jss";
import useStyles from "./InputBox.styles";
import { FontAwesomeIcon } from "./FontAwesome";
import InputWrapper from "./InputWrapper";
import * as InputTokens from "../../theme/inputThemeTokens";
import type { Theme } from "../../types/Theme";
import { wasLastInteractionKeyboard } from "../../utils/inputModality";

interface Props {
    freeform?: boolean;
    size?: string;
    leftLabel?: string;
    rightLabel?: string;
    className?: string;
    style?: React.CSSProperties;
    accessoryLeft?: IconProp;
    accessoryRight?: IconProp;
    /** When set, the right accessory receives pointer events (e.g. open calendar on date fields). */
    onAccessoryRightClick?: () => void;
    accessoryRightAriaLabel?: string;
    children: unknown;
    disabled?: boolean;
    /** Hide the native textarea resize grip (e.g. open-end fields). */
    disableTextareaResize?: boolean;
    /** Modern HybridGrid cells: omit InputWrapper block-start margin. */
    suppressWrapperTopMargin?: boolean;
}

function enhanceFocusableChild(
    child: React.ReactElement,
    handlers: {
        onFocus: (e: React.FocusEvent) => void;
        onBlur: (e: React.FocusEvent) => void;
    },
): React.ReactElement {
    const origOnFocus = child.props.onFocus as React.FocusEventHandler | undefined;
    const origOnBlur = child.props.onBlur as React.FocusEventHandler | undefined;

    return React.cloneElement(child, {
        onFocus: (e: React.FocusEvent) => {
            handlers.onFocus(e);
            origOnFocus?.(e);
        },
        onBlur: (e: React.FocusEvent) => {
            handlers.onBlur(e);
            origOnBlur?.(e);
        },
    });
}

export default function InputBox(props: Props) {
    const {
        freeform,
        children,
        className,
        style,
        accessoryLeft,
        accessoryRight,
        onAccessoryRightClick,
        accessoryRightAriaLabel,
        size,
        leftLabel,
        rightLabel,
        disabled,
        disableTextareaResize = false,
        suppressWrapperTopMargin = false,
    } = props;
    const theme = useTheme() as Theme;
    const useSplitFocusStates = InputTokens.resolveTextInputFocusRingStroke(theme) > 0;

    const [hovered, setHovered] = React.useState(false);
    const [keyboardFocused, setKeyboardFocused] = React.useState(false);
    const [domFocused, setDomFocused] = React.useState(false);

    const editing = domFocused;
    const borderHighlighted = hovered || domFocused;
    const showFocusRing = useSplitFocusStates && keyboardFocused;

    const classes = useStyles({
        theme,
        focused: showFocusRing,
        hovered,
        editing,
        borderHighlighted,
        freeform,
        accessoryLeft,
        accessoryRight,
        disableTextareaResize,
    });

    const focusHandlers = {
        onFocus: () => {
            setDomFocused(true);
            if (useSplitFocusStates) {
                setKeyboardFocused(wasLastInteractionKeyboard());
            }
        },
        onBlur: () => {
            setDomFocused(false);
            setKeyboardFocused(false);
        },
    };

    const enhancedChildren = React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) {
            return child;
        }
        const type = child.type;
        if (type !== "input" && type !== "textarea" && type !== "button" && type !== "select") {
            return child;
        }
        return enhanceFocusableChild(child, focusHandlers);
    });

    const accessoryRightNode = accessoryRight && (
        <FontAwesomeIcon
            icon={accessoryRight}
            className={classnames(
                classes.inputBoxAccessory,
                classes.inputBoxAccessoryRight,
                onAccessoryRightClick && classes.inputBoxAccessoryRightInteractive,
            )}
            role={onAccessoryRightClick ? "button" : undefined}
            tabIndex={onAccessoryRightClick && !disabled ? 0 : undefined}
            aria-label={onAccessoryRightClick ? accessoryRightAriaLabel : undefined}
            onClick={
                onAccessoryRightClick && !disabled
                    ? (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onAccessoryRightClick();
                      }
                    : undefined
            }
            onKeyDown={
                onAccessoryRightClick && !disabled
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              onAccessoryRightClick();
                          }
                      }
                    : undefined
            }
        />
    );

    const accessoryLeftNode = accessoryLeft && (
        <FontAwesomeIcon
            icon={accessoryLeft}
            className={classnames(classes.inputBoxAccessory, classes.inputBoxAccessoryLeft)}
        />
    );

    const innerContent = (
        <>
            {accessoryLeftNode}
            {enhancedChildren}
            {accessoryRightNode}
        </>
    );

    return (
        <InputWrapper
            size={size}
            leftLabel={leftLabel}
            rightLabel={rightLabel}
            suppressTopMargin={suppressWrapperTopMargin}
        >
            <div
                className={classnames(
                    classes.inputBox,
                    showFocusRing && classes.inputBoxFocused,
                    disabled && classes.inputBoxDisabled,
                    className,
                )}
                style={style}
                onMouseEnter={() => !disabled && setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                {innerContent}
            </div>
        </InputWrapper>
    );
}
